import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Plus, Edit, Trash2, Eye, Package, RefreshCw, Search,
    Users, UserCheck, UserX, Check, Archive, ChevronDown, ChevronUp,
    Download, FileSpreadsheet, FileText,
    Ban
} from 'lucide-react';
import { usePermissions } from '../../view/Permissions';
import {
    PlanService, generateMockUsersForPlans, attachSubscriberStats,
    Panel, StatCard, StatusBadge, PlanTypeBadge, ErrorBanner
} from './PlanShared';

// ── Export helpers ────────────────────────────────────────────────────────────

const EXPORT_COLUMNS = [
    { key: 'name', label: 'Plan Name' },
    { key: 'description', label: 'Description' },
    { key: 'price', label: 'Price' },
    { key: 'planType', label: 'Plan Type' },
    { key: 'discountType', label: 'Discount Type' },
    { key: 'discountAmount', label: 'Discount Amount' },
    { key: 'features', label: 'Features' },
    { key: 'status', label: 'Status' },
    { key: 'subscriberCount', label: 'Total Subscribers' },
    { key: 'activeUsers', label: 'Active Users' },
    { key: 'inactiveUsers', label: 'Inactive Users' },
    { key: 'createdAt', label: 'Created At' },
    { key: 'updatedAt', label: 'Updated At' },
];

const toExportRow = (plan) => ({
    name: plan.name,
    description: plan.description,
    price: plan.price,
    planType: plan.planType === 'yearly' ? 'Yearly' : 'Monthly',
    discountType: plan.isDiscountApplicable ? plan.discountType : '—',
    discountAmount: plan.isDiscountApplicable ? plan.discountAmount : 0,
    features: (plan.features || []).join('; '),
    status: plan.status,
    subscriberCount: plan.subscriberCount || 0,
    activeUsers: plan.activeUsers || 0,
    inactiveUsers: plan.inactiveUsers || 0,
    createdAt: plan.createdAt ? new Date(plan.createdAt).toLocaleString() : '',
    updatedAt: plan.updatedAt ? new Date(plan.updatedAt).toLocaleString() : '',
});

const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const timestampedFilename = (ext) => {
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    return `plans-export-${stamp}.${ext}`;
};

// Pure client-side CSV export - no external dependency needed.
const exportPlansToCSV = (plans) => {
    const escapeCell = (value) => {
        const str = String(value ?? '');
        return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const header = EXPORT_COLUMNS.map(c => escapeCell(c.label)).join(',');
    const rows = plans.map(plan => {
        const row = toExportRow(plan);
        return EXPORT_COLUMNS.map(c => escapeCell(row[c.key])).join(',');
    });
    const csv = [header, ...rows].join('\r\n');
    // BOM so Excel opens UTF-8 (₹ etc.) correctly
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, timestampedFilename('csv'));
};

// True .xlsx export via SheetJS. Requires the `xlsx` package:
//   npm install xlsx
const exportPlansToExcel = async (plans) => {
    const XLSX = await import('xlsx');
    const rows = plans.map(toExportRow);
    const worksheet = XLSX.utils.json_to_sheet(rows, { header: EXPORT_COLUMNS.map(c => c.key) });
    XLSX.utils.sheet_add_aoa(worksheet, [EXPORT_COLUMNS.map(c => c.label)], { origin: 'A1' });
    worksheet['!cols'] = EXPORT_COLUMNS.map(c => ({ wch: Math.max(c.label.length + 2, 14) }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Plans');
    XLSX.writeFile(workbook, timestampedFilename('xlsx'));
};

// ── Export button with a small dropdown ───────────────────────────────────────

const ExportMenu = ({ plans, disabled }) => {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const runExport = async (fn) => {
        setBusy(true);
        try {
            await fn(plans);
        } catch (err) {
            window.alert(err.message || 'Export failed. Please try again.');
        } finally {
            setBusy(false);
            setOpen(false);
        }
    };

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                disabled={disabled || busy || plans.length === 0}
                onClick={() => setOpen(o => !o)}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-colors bg-orange-50 text-orange-500 border border-orange-200 hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <Download size={16} /> {busy ? 'Exporting...' : 'Export'} <ChevronDown size={14} />
            </button>

            {open && (
                <div className="absolute right-0 mt-2 w-48 rounded-xl bg-white border border-orange-200 shadow-lg overflow-hidden z-20">
                    <button
                        type="button"
                        onClick={() => runExport(exportPlansToCSV)}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-orange-50 transition-colors"
                    >
                        <FileText size={16} className="text-orange-500" /> Export as CSV
                    </button>
                    <button
                        type="button"
                        onClick={() => runExport(exportPlansToExcel)}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-orange-50 transition-colors border-t border-orange-100"
                    >
                        <FileSpreadsheet size={16} className="text-orange-500" /> Export as Excel (.xlsx)
                    </button>
                </div>
            )}
        </div>
    );
};

// ── Plan Card (Mobile) ──────────────────────────────────────────────────────

const PlanCard = ({ plan, onView, onEdit, onDelete, onToggleStatus, onViewActive, onViewInactive, busy }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <Panel className="p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${plan.status === 'active' ? 'from-orange-500 to-amber-600' : 'from-gray-400 to-gray-500'} flex items-center justify-center shrink-0`}>
                        <Package size={16} className="text-white" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-semibold truncate text-gray-800">{plan.name}</p>
                        <p className="text-xs truncate text-gray-500">{plan.description}</p>
                    </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                    <StatusBadge status={plan.status} />
                    <PlanTypeBadge planType={plan.planType} />
                </div>
            </div>

            <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-xl py-2 bg-orange-50">
                    <p className="text-sm font-bold text-orange-500">₹{plan.price}</p>
                    <p className="text-[10px] text-gray-500">Price</p>
                </div>
                <div className="rounded-xl py-2 bg-orange-50">
                    <p className="text-sm font-bold text-orange-500">{plan.features.length}</p>
                    <p className="text-[10px] text-gray-500">Features</p>
                </div>
                <div className="rounded-xl py-2 bg-orange-50">
                    <p className="text-sm font-bold text-orange-500">{plan.subscriberCount || 0}</p>
                    <p className="text-[10px] text-gray-500">Users</p>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <button onClick={() => onViewActive(plan)} className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${plan.activeUsers > 0 ? 'hover:bg-orange-50' : 'opacity-50 cursor-not-allowed'}`}
                    style={plan.activeUsers > 0 ? { background: 'rgba(255,125,56,0.12)', color: '#FF7D38' } : { background: 'rgba(100,116,139,0.08)', color: '#94a3b8' }}>
                    <UserCheck size={12} /> {plan.activeUsers || 0} Active
                </button>
                <button onClick={() => onViewInactive(plan)} className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${plan.inactiveUsers > 0 ? 'hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'}`}
                    style={plan.inactiveUsers > 0 ? { background: 'rgba(100,116,139,0.12)', color: '#64748b' } : { background: 'rgba(100,116,139,0.08)', color: '#94a3b8' }}>
                    <UserX size={12} /> {plan.inactiveUsers || 0} Inactive
                </button>
                <button onClick={() => setExpanded(!expanded)} className="ml-auto p-1 rounded-lg transition-colors text-gray-400 hover:text-orange-500">
                    {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
            </div>

            {expanded && (
                <div className="pt-3 space-y-2 border-t border-orange-100">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>Updated: {new Date(plan.updatedAt).toLocaleDateString()}</span>
                        <span className="truncate max-w-[140px]">ID: {plan.id}</span>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <button onClick={() => onView(plan)} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg transition-colors text-orange-500 hover:bg-orange-50">
                            <Eye size={14} /> View
                        </button>
                        <button onClick={() => onEdit(plan)} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg transition-colors text-orange-500 hover:bg-orange-50">
                            <Edit size={14} /> Edit
                        </button>
                        <button disabled={busy} onClick={() => onToggleStatus(plan)} className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg transition-colors disabled:opacity-50 ${plan.status === 'active' ? 'text-amber-500 hover:bg-amber-50' : 'text-orange-500 hover:bg-orange-50'}`}>
                            {plan.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                        <button disabled={busy} onClick={() => onDelete(plan.id)} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg transition-colors ml-auto text-red-500 hover:bg-red-50 disabled:opacity-50">
                            <Trash2 size={14} /> Delete
                        </button>
                    </div>
                </div>
            )}
        </Panel>
    );
};

// ── Main Component ────────────────────────────────────────────────────────────

const AllPlans = () => {
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('All');
    const [filterPlanType, setFilterPlanType] = useState('All');

    const {
        canView,
        canAdd,
        canEdit,
        canDelete,
        canDownload,
        canExport
    } = usePermissions();

    const loadPlans = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await PlanService.getAll();
            generateMockUsersForPlans(data);
            setPlans(data.map(attachSubscriberStats));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadPlans(); }, [loadPlans]);

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this plan? This action cannot be undone.')) return;
        setBusyId(id);
        try {
            await PlanService.delete(id);
            setPlans(prev => prev.filter(p => p.id !== id));
        } catch (err) {
            window.alert(err.message);
        } finally {
            setBusyId(null);
        }
    };

    const handleToggleStatus = async (plan) => {
        const newStatus = plan.status === 'active' ? 'inactive' : 'active';
        setBusyId(plan.id);
        try {
            const updated = await PlanService.update(plan.id, { ...plan, status: newStatus });
            setPlans(prev => prev.map(p => p.id === updated.id ? attachSubscriberStats(updated) : p));
        } catch (err) {
            window.alert(err.message);
        } finally {
            setBusyId(null);
        }
    };

    const goToView = (plan) => navigate(`/admin/plans/${plan.id}`);
    const goToEdit = (plan) => navigate(`/admin/plans/${plan.id}/edit`);
    const goToCreate = () => navigate('/admin/plans/new');
    const handleViewActiveUsers = (plan) => navigate(`/admin/plans/${plan.id}/active-users`);
    const handleViewInactiveUsers = (plan) => navigate(`/admin/plans/${plan.id}/inactive-users`);

    const filtered = useMemo(() => {
        return plans.filter(plan => {
            const matchSearch = plan.name.toLowerCase().includes(search.toLowerCase()) ||
                plan.description.toLowerCase().includes(search.toLowerCase());
            const matchStatus = filterStatus === 'All' || plan.status === filterStatus;
            const matchPlanType = filterPlanType === 'All' || plan.planType === filterPlanType;
            return matchSearch && matchStatus && matchPlanType;
        });
    }, [plans, search, filterStatus, filterPlanType]);

    const stats = useMemo(() => [
        { label: 'Total Plans', value: plans.length, icon: Package, grad: 'from-orange-500 to-amber-600' },
        { label: 'Active Plans', value: plans.filter(p => p.status === 'active').length, icon: Check, grad: 'from-amber-400 to-orange-500' },
        { label: 'Inactive Plans', value: plans.filter(p => p.status === 'inactive').length, icon: Archive, grad: 'from-gray-400 to-gray-500' },
        { label: 'Total Subscribers', value: plans.reduce((acc, p) => acc + (p.subscriberCount || 0), 0), icon: Users, grad: 'from-yellow-500 to-amber-600' },
    ], [plans]);

    return (
        <div className="space-y-4 sm:space-y-6 px-2 sm:px-0 bg-gray-50 min-h-screen p-4 rounded-2xl">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 rounded-2xl border border-orange-200 shadow-sm">
                <div>
                    <h1 className="text-lg sm:text-xl font-bold text-gray-800">Plan Management</h1>
                    <p className="text-xs sm:text-sm mt-0.5 text-gray-500">Create, manage, and customize subscription plans</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {canExport('Plans') && (
                        <ExportMenu plans={filtered} disabled={loading} />
                    )}
                    {canAdd('Plans') && (
                        <button onClick={goToCreate}
                            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white rounded-xl transition-all shadow-sm bg-gradient-to-r from-orange-500 to-orange-600 hover:shadow-orange-500/30">
                            <Plus size={16} /> New Plan
                        </button>
                    )}
                </div>
            </div>

            {error && <ErrorBanner message={error} onRetry={loadPlans} />}

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
                {stats.map(s => <StatCard key={s.label} {...s} />)}
            </div>

            {/* Filters */}
            <Panel>
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input type="text" placeholder="Search plans..." value={search} onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-9 pr-3.5 py-2.5 text-sm rounded-xl transition-all bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200" />
                    </div>
                    <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                        className="px-3.5 py-2.5 text-sm rounded-xl transition-all bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200">
                        {['All', 'active', 'inactive'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select value={filterPlanType} onChange={(e) => setFilterPlanType(e.target.value)}
                        className="px-3.5 py-2.5 text-sm rounded-xl transition-all bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200">
                        <option value="All">All Types</option>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                    </select>
                    <button onClick={loadPlans} className="flex items-center justify-center gap-2 px-3.5 py-2.5 text-sm rounded-xl transition-colors bg-orange-50 text-orange-500 border border-orange-200 hover:bg-orange-100">
                        <RefreshCw size={14} /> <span className="hidden sm:inline">Refresh</span>
                    </button>
                </div>
            </Panel>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
                {loading ? (
                    <div className="text-center py-16">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-orange-400 border-t-transparent mx-auto" />
                        <p className="text-sm mt-4 text-gray-500">Loading plans...</p>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-16 rounded-2xl bg-white border border-orange-200">
                        <Package size={32} className="mx-auto mb-3 opacity-30 text-gray-400" />
                        <p className="text-sm text-gray-500">No plans match your filters</p>
                    </div>
                ) : filtered.map(plan => (
                    <PlanCard key={plan.id} plan={plan} onView={goToView} onEdit={goToEdit} onDelete={handleDelete}
                        onToggleStatus={handleToggleStatus} onViewActive={handleViewActiveUsers} onViewInactive={handleViewInactiveUsers}
                        busy={busyId === plan.id} />
                ))}
            </div>

            {/* Desktop Table */}
            <Panel className="hidden md:block overflow-hidden p-0">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-orange-200 bg-orange-50">
                                <th className="text-left text-xs font-semibold px-5 py-3.5 text-gray-600">Plan</th>
                                <th className="text-left text-xs font-semibold px-4 py-3.5 text-gray-600">Price</th>
                                <th className="text-left text-xs font-semibold px-4 py-3.5 text-gray-600">Type</th>
                                <th className="text-left text-xs font-semibold px-4 py-3.5 text-gray-600">Discount</th>
                                <th className="text-left text-xs font-semibold px-4 py-3.5 text-gray-600">Features</th>
                                <th className="text-left text-xs font-semibold px-4 py-3.5 text-gray-600">Subscribers</th>
                                <th className="text-left text-xs font-semibold px-4 py-3.5 text-gray-600">Status</th>
                                <th className="text-left text-xs font-semibold px-4 py-3.5 text-gray-600">Updated</th>
                                <th className="text-xs font-semibold px-4 py-3.5 text-gray-600">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-orange-100">
                            {loading ? (
                                <tr>
                                    <td colSpan={9} className="text-center py-16">
                                        <div className="flex flex-col items-center gap-2">
                                            <div className="animate-spin rounded-full h-8 w-8 border-2 border-orange-400 border-t-transparent" />
                                            <p className="text-sm text-gray-500">Loading plans...</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={9} className="text-center py-16">
                                        <div className="flex flex-col items-center gap-2">
                                            <Package size={32} className="opacity-30 text-gray-400" />
                                            <p className="text-sm text-gray-500">No plans match your filters</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : filtered.map(plan => (
                                <tr key={plan.id} className="hover:bg-orange-50 transition-colors">
                                    <td className="px-5 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${plan.status === 'active' ? 'from-orange-500 to-amber-600' : 'from-gray-400 to-gray-500'} flex items-center justify-center shrink-0`}>
                                                <Package size={14} className="text-white" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold text-gray-800">{plan.name}</p>
                                                <p className="text-xs truncate max-w-[180px] text-gray-500">{plan.description}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4 text-sm font-bold text-orange-500">₹{plan.price}</td>
                                    <td className="px-4 py-4">
                                        <PlanTypeBadge planType={plan.planType} />
                                    </td>
                                    <td className="px-4 py-4">
                                        {plan.isDiscountApplicable && plan.discountAmount > 0 ? (
                                            <span className="text-xs font-medium px-2 py-1 rounded-full bg-orange-50 text-orange-500 border border-orange-200">
                                                {plan.discountType === 'percentage' ? `${plan.discountAmount}%` : `$${plan.discountAmount}`}
                                            </span>
                                        ) : <span className="text-xs text-gray-400">—</span>}
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-700">{plan.features.length}</td>
                                    <td className="px-4 py-4">
                                        <div className="flex items-center gap-1.5">
                                            <button onClick={() => handleViewActiveUsers(plan)}
                                                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${plan.activeUsers > 0 ? 'hover:bg-orange-50' : 'opacity-50 cursor-not-allowed'}`}
                                                style={plan.activeUsers > 0 ? { background: 'rgba(255,125,56,0.12)', color: '#FF7D38' } : { background: 'rgba(100,116,139,0.08)', color: '#94a3b8' }}>
                                                <UserCheck size={12} /> {plan.activeUsers || 0}
                                            </button>
                                            <button onClick={() => handleViewInactiveUsers(plan)}
                                                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${plan.inactiveUsers > 0 ? 'hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'}`}
                                                style={plan.inactiveUsers > 0 ? { background: 'rgba(100,116,139,0.12)', color: '#64748b' } : { background: 'rgba(100,116,139,0.08)', color: '#94a3b8' }}>
                                                <UserX size={12} /> {plan.inactiveUsers || 0}
                                            </button>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4">
                                        <button disabled={busyId === plan.id} onClick={() => handleToggleStatus(plan)}
                                            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors disabled:opacity-50 ${plan.status === 'active' ? 'bg-orange-50 text-orange-500 border border-orange-200' : 'bg-gray-100 text-gray-500'}`}>
                                            <span className={`w-1.5 h-1.5 rounded-full ${plan.status === 'active' ? 'bg-orange-500 animate-pulse' : 'bg-gray-400'}`} />
                                            {plan.status}
                                        </button>
                                    </td>
                                    <td className="px-4 py-4 text-xs text-gray-500">{new Date(plan.updatedAt).toLocaleDateString()}</td>
                                    <td className="px-4 py-4">
                                        {canView('Plans') || canEdit('Plans') || canDelete('Plans') ? (
                                            <div className="flex items-center gap-1.5">
                                                {canView('Plans') && (
                                                    <button onClick={() => goToView(plan)}
                                                        className="p-1.5 rounded-lg transition-colors text-gray-400 hover:text-orange-500 hover:bg-orange-50">
                                                        <Eye size={16} />
                                                    </button>
                                                )}
                                                {canEdit('Plans') && (
                                                    <button onClick={() => goToEdit(plan)}
                                                        className="p-1.5 rounded-lg transition-colors text-gray-400 hover:text-orange-500 hover:bg-orange-50">
                                                        <Edit size={16} />
                                                    </button>
                                                )}
                                                {canDelete('Plans') && (
                                                    <button disabled={busyId === plan.id} onClick={() => handleDelete(plan.id)}
                                                        className="p-1.5 rounded-lg transition-colors text-gray-400 hover:text-red-500 hover:bg-red-50 disabled:opacity-50">
                                                        <Trash2 size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-all bg-gray-100 text-gray-400 border border-gray-200">
                                                <Ban size={13} className="bg-gray-100 text-red-500" /> no access
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="px-5 py-3 border-t border-orange-200 flex items-center justify-between bg-orange-50">
                    <p className="text-xs text-gray-500">Showing {filtered.length} of {plans.length} plans</p>
                </div>
            </Panel>
        </div>
    );
};

export default AllPlans;